import * as d3 from 'd3'
import * as React from 'react'

import { cn } from '@/utils'
import { CHART_COLORS } from './Constants'
import { ChartControls } from './Controls'
import { useChartBase } from './Hooks'
import { ChartTooltip } from './Tooltip'
import type { DateRangeType, Filter, SchemaField } from './utils/exportChart'
import {
  EXTERNAL_NAME_PATTERN,
  formatCompactCurrency,
} from './utils/formatters'

interface NetworkNodeData {
  name: string
  color?: string
}

interface NetworkLinkData {
  source: number
  target: number
  value: number
}

interface NetworkChartData {
  nodes: NetworkNodeData[]
  links: NetworkLinkData[]
}

type SimNode = d3.SimulationNodeDatum &
  NetworkNodeData & { index: number; radius: number }
type SimLink = {
  source: SimNode
  target: SimNode
  value: number
  curveSign: 1 | -1
}

interface NetworkChartProps extends React.HTMLAttributes<HTMLDivElement> {
  data?: NetworkChartData
  className?: string
  height?: number
  margin?: { top: number; right: number; bottom: number; left: number }
  nodeRadius?: number
  onChartReady?: (api: {
    containerRef: React.RefObject<HTMLDivElement>
    isFullscreen: boolean
    toggleFullscreen: () => Promise<void>
  }) => void
  projectName?: string
  title?: string
  filters?: Filter[]
  schema?: SchemaField[]
  dateRange?: DateRangeType
  filename?: string
  showHoverControls?: boolean
}

type HoveredNode = { type: 'node'; node: SimNode }
type HoveredLink = { type: 'link'; link: SimLink }
type HoveredData = HoveredNode | HoveredLink | null

const DEFAULT_DATA: NetworkChartData = { nodes: [], links: [] }
const DEFAULT_MARGIN = { top: 40, right: 80, bottom: 40, left: 80 }

const EXTERNAL_FILL = '#94a3b8' // slate-400

const OUTGOING_STROKE = '#dc2626' // red-600 — money leaving the focal node
const INCOMING_STROKE = '#059669' // emerald-600 — money arriving at the focal

const NetworkChart = React.forwardRef<HTMLDivElement, NetworkChartProps>(
  (
    {
      data = DEFAULT_DATA,
      className,
      height: _height = 400,
      margin = DEFAULT_MARGIN,
      nodeRadius = 20,
      onChartReady,
      projectName,
      title,
      filters,
      schema,
      dateRange,
      filename = 'chart-export.png',
      showHoverControls = false,
      ...props
    },
    _ref,
  ) => {
    const {
      containerRef,
      dimensions,
      isFullscreen,
      toggleFullscreen,
      fullscreenError: error,
      fullscreenSupported: isSupported,
    } = useChartBase(onChartReady)
    const [hoveredData, setHoveredData] = React.useState<HoveredData>(null)
    const [, forceRerender] = React.useReducer((value: number) => value + 1, 0)

    const simNodesRef = React.useRef<SimNode[]>([])
    const simLinksRef = React.useRef<SimLink[]>([])
    const simulationRef = React.useRef<d3.Simulation<SimNode, SimLink> | null>(
      null,
    )

    const colorScale = React.useMemo(() => d3.scaleOrdinal(CHART_COLORS), [])

    const dataKey = React.useMemo(() => {
      const nodeKey = data.nodes.map((node) => node.name).join('|')
      const linkKey = data.links
        .map((link) => `${link.source}>${link.target}:${link.value}`)
        .join('|')
      return `${nodeKey}#${linkKey}`
    }, [data])

    const linkWidthScale = React.useMemo(() => {
      const values = data.links.map((link) => link.value)
      if (values.length === 0) return defaultLinkWidth
      const max = Math.max(...values)
      const min = Math.min(...values)
      const domain = max === min ? [0, max || 1] : [min, max]
      return d3.scaleLinear<number>().domain(domain).range([1.5, 7]).clamp(true)
    }, [data.links])

    // Per-node radius: scale by total flow (in + out) so high-volume
    // accounts visually dominate. sqrt keeps the largest from blowing up.
    const nodeRadiusScale = React.useMemo(() => {
      const totals = computeNodeTotals(data)
      const values = Array.from(totals.values())
      if (values.length === 0) return defaultRadiusScale(nodeRadius)
      const max = Math.max(...values)
      const min = Math.min(...values)
      const domain = max === min ? [0, max || 1] : [min, max]
      const scale = d3
        .scaleSqrt<number>()
        .domain(domain)
        .range([nodeRadius * 0.7, nodeRadius * 1.6])
        .clamp(true)
      return (name: string) => scale(totals.get(name) ?? 0)
    }, [data, nodeRadius])

    const previousPositionsRef = React.useRef<
      Map<string, { x: number; y: number }>
    >(new Map())

    React.useEffect(() => {
      if (dimensions.width <= 0 || dimensions.height <= 0) return
      if (data.nodes.length === 0) return

      const centerX = dimensions.width / 2
      const centerY = dimensions.height / 2
      const radius =
        Math.min(
          dimensions.width - margin.left - margin.right,
          dimensions.height - margin.top - margin.bottom,
        ) / 2.5

      const prev = previousPositionsRef.current
      const simNodes: SimNode[] = data.nodes.map((node, index) => {
        const cached = prev.get(node.name)
        const angle = (index / Math.max(data.nodes.length, 1)) * 2 * Math.PI
        return {
          ...node,
          index,
          radius: nodeRadiusScale(node.name),
          x: cached?.x ?? centerX + Math.cos(angle) * radius,
          y: cached?.y ?? centerY + Math.sin(angle) * radius,
        }
      })

      const pairSeen = new Map<string, number>()
      const simLinks: SimLink[] = data.links.map((link) => {
        const pairKey =
          link.source < link.target
            ? `${link.source}-${link.target}`
            : `${link.target}-${link.source}`
        const seen = pairSeen.get(pairKey) ?? 0
        pairSeen.set(pairKey, seen + 1)
        const curveSign: 1 | -1 = seen === 0 ? 1 : -1
        return {
          source: simNodes[link.source],
          target: simNodes[link.target],
          value: link.value,
          curveSign,
        }
      })

      simNodesRef.current = simNodes
      simLinksRef.current = simLinks

      const maxRadius = simNodes.reduce(
        (acc, node) => Math.max(acc, node.radius),
        nodeRadius,
      )

      const simulation = d3
        .forceSimulation<SimNode>(simNodes)
        .force(
          'link',
          d3
            .forceLink<SimNode, SimLink>(simLinks)
            .id((node) => node.index)
            .distance(Math.max(140, radius))
            .strength(0.5),
        )
        .force('charge', d3.forceManyBody().strength(-1400))
        .force('center', d3.forceCenter(centerX, centerY))
        .force(
          'collision',
          d3
            .forceCollide<SimNode>()
            .radius((node) => node.radius * 2.2)
            .strength(1),
        )
        .alpha(1)
        .alphaDecay(0.03)

      const padX = maxRadius + 90
      const padTop = maxRadius + 32
      const padBottom = maxRadius + 18
      simulation.on('tick', () => {
        for (const node of simNodes) {
          if (typeof node.x !== 'number' || typeof node.y !== 'number') continue
          node.x = Math.max(padX, Math.min(dimensions.width - padX, node.x))
          node.y = Math.max(
            padTop,
            Math.min(dimensions.height - padBottom, node.y),
          )
          previousPositionsRef.current.set(node.name, { x: node.x, y: node.y })
        }
        forceRerender()
      })
      simulationRef.current = simulation

      return () => {
        simulation.stop()
        simulationRef.current = null
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataKey, dimensions.width, dimensions.height, margin, nodeRadius])

    const nodes = simNodesRef.current
    const links = simLinksRef.current

    const focalIndex =
      hoveredData?.type === 'node' ? hoveredData.node.index : null
    const hasFocal = focalIndex !== null

    return (
      <div
        ref={containerRef}
        className={cn('w-full h-full relative group', className)}
        {...props}
      >
        {nodes.length > 0 && dimensions.width > 0 && dimensions.height > 0 && (
          <svg
            role="img"
            aria-label="Network chart"
            width={dimensions.width}
            height={dimensions.height}
            viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
          >
            <defs>
              <ArrowheadMarker
                id="network-arrowhead"
                colorClass="fill-foreground"
              />
              <ArrowheadMarker
                id="network-arrowhead-out"
                color={OUTGOING_STROKE}
              />
              <ArrowheadMarker
                id="network-arrowhead-in"
                color={INCOMING_STROKE}
              />
            </defs>
            <g>
              {links.map((link, i) => {
                const arc = buildArcPath(link)
                if (!arc) return null
                const isHovered =
                  hoveredData?.type === 'link' && hoveredData.link === link
                const role = linkRole(link, focalIndex)
                const stroke = strokeForRole(role)
                const markerId = markerForRole(role)
                const opacity = linkOpacity({
                  role,
                  hasFocal,
                  isHovered,
                })
                return (
                  <g key={`link-${i}`}>
                    <path
                      d={arc.path}
                      fill="none"
                      stroke={stroke ?? 'currentColor'}
                      strokeWidth={linkWidthScale(link.value)}
                      strokeOpacity={opacity}
                      className={cn(
                        'transition-opacity duration-150 cursor-pointer',
                        stroke ? '' : 'stroke-foreground/70',
                      )}
                      markerEnd={`url(#${markerId})`}
                      onPointerEnter={() =>
                        setHoveredData({ type: 'link', link })
                      }
                      onPointerLeave={() => setHoveredData(null)}
                    />
                    <LinkValueLabel
                      x={arc.midX}
                      y={arc.midY}
                      value={link.value}
                      visible={opacity > 0.3}
                    />
                  </g>
                )
              })}
            </g>
            <g>
              {nodes.map((node, i) => {
                const fill = nodeFill(node, colorScale)
                const dimmed = hasFocal && !isConnected(node, links, focalIndex)
                return (
                  <g
                    key={`node-${i}`}
                    transform={`translate(${node.x ?? 0},${node.y ?? 0})`}
                    className="cursor-pointer transition-opacity duration-150"
                    opacity={dimmed ? 0.25 : 1}
                    onPointerEnter={() =>
                      setHoveredData({ type: 'node', node })
                    }
                    onPointerLeave={() => setHoveredData(null)}
                  >
                    <circle
                      r={node.radius}
                      fill={fill}
                      fillOpacity={0.92}
                      stroke="currentColor"
                      strokeWidth={2}
                      className="text-background"
                    />
                    <text
                      y={-(node.radius + 8)}
                      textAnchor="middle"
                      fontSize={isFullscreen ? '13' : '11'}
                      fontWeight="600"
                      className="fill-foreground select-none pointer-events-none"
                      paintOrder="stroke"
                      stroke="var(--background)"
                      strokeWidth={3}
                      strokeLinejoin="round"
                    >
                      {node.name}
                    </text>
                  </g>
                )
              })}
            </g>
          </svg>
        )}

        {showHoverControls && !onChartReady && (
          <ChartControls
            containerRef={containerRef}
            isFullscreen={isFullscreen}
            toggleFullscreen={toggleFullscreen}
            fullscreenError={error}
            fullscreenSupported={isSupported}
            filename={filename}
            projectName={projectName}
            title={title}
            filters={filters}
            schema={schema}
            dateRange={dateRange}
          />
        )}

        <ChartTooltip
          active={!!hoveredData}
          position={tooltipPosition(hoveredData)}
        >
          {hoveredData && hoveredData.type === 'node' && (
            <div className="font-semibold">{hoveredData.node.name}</div>
          )}
          {hoveredData && hoveredData.type === 'link' && (
            <>
              <div className="font-semibold">
                {hoveredData.link.source.name} → {hoveredData.link.target.name}
              </div>
              <div className="text-muted-foreground opacity-80">
                Value: {hoveredData.link.value.toLocaleString()}
              </div>
            </>
          )}
        </ChartTooltip>
      </div>
    )
  },
)
NetworkChart.displayName = 'NetworkChart'

interface ArrowheadMarkerProps {
  id: string
  color?: string
  colorClass?: string
}

function ArrowheadMarker({ id, color, colorClass }: ArrowheadMarkerProps) {
  return (
    <marker
      id={id}
      viewBox="0 -5 10 10"
      refX={10}
      refY={0}
      markerWidth={8}
      markerHeight={8}
      markerUnits="userSpaceOnUse"
      orient="auto"
      fill={color}
      className={colorClass}
    >
      <path d="M0,-5L10,0L0,5" />
    </marker>
  )
}

interface LinkValueLabelProps {
  x: number
  y: number
  value: number
  visible: boolean
}

function LinkValueLabel({ x, y, value, visible }: LinkValueLabelProps) {
  if (!visible) return null
  return (
    <text
      x={x}
      y={y}
      textAnchor="middle"
      dominantBaseline="middle"
      fontSize="10"
      fontWeight="600"
      className="fill-foreground select-none pointer-events-none"
      paintOrder="stroke"
      stroke="var(--background)"
      strokeWidth={3}
      strokeLinejoin="round"
    >
      {formatCompactCurrency(value)}
    </text>
  )
}

type LinkRole = 'outgoing' | 'incoming' | 'unrelated' | 'neutral'

function linkRole(link: SimLink, focalIndex: number | null): LinkRole {
  if (focalIndex === null) return 'neutral'
  if (link.source.index === focalIndex) return 'outgoing'
  if (link.target.index === focalIndex) return 'incoming'
  return 'unrelated'
}

function strokeForRole(role: LinkRole): string | null {
  if (role === 'outgoing') return OUTGOING_STROKE
  if (role === 'incoming') return INCOMING_STROKE
  return null
}

function markerForRole(role: LinkRole): string {
  if (role === 'outgoing') return 'network-arrowhead-out'
  if (role === 'incoming') return 'network-arrowhead-in'
  return 'network-arrowhead'
}

interface LinkOpacityArgs {
  role: LinkRole
  hasFocal: boolean
  isHovered: boolean
}

function linkOpacity({ role, hasFocal, isHovered }: LinkOpacityArgs): number {
  if (isHovered) return 0.95
  if (!hasFocal) return 0.6
  if (role === 'unrelated') return 0.1
  return 0.85
}

function isConnected(
  node: SimNode,
  links: SimLink[],
  focalIndex: number | null,
): boolean {
  if (focalIndex === null) return true
  if (node.index === focalIndex) return true
  return links.some((link) => {
    if (link.source.index === focalIndex && link.target.index === node.index)
      return true
    if (link.target.index === focalIndex && link.source.index === node.index)
      return true
    return false
  })
}

function nodeFill(
  node: NetworkNodeData,
  colorScale: d3.ScaleOrdinal<string, string>,
): string {
  if (node.color) return node.color
  if (EXTERNAL_NAME_PATTERN.test(node.name)) return EXTERNAL_FILL
  return colorScale(node.name) as string
}

function computeNodeTotals(data: NetworkChartData): Map<string, number> {
  const totals = new Map<string, number>()
  for (const link of data.links) {
    const sourceName = data.nodes[link.source]?.name
    const targetName = data.nodes[link.target]?.name
    if (sourceName)
      totals.set(sourceName, (totals.get(sourceName) ?? 0) + link.value)
    if (targetName)
      totals.set(targetName, (totals.get(targetName) ?? 0) + link.value)
  }
  return totals
}

function defaultLinkWidth(): number {
  return 2
}

function defaultRadiusScale(baseRadius: number): (name: string) => number {
  return function constantRadius(_name: string) {
    return baseRadius
  }
}

interface ArcPath {
  path: string
  midX: number
  midY: number
}

function buildArcPath(link: SimLink): ArcPath | null {
  const sourceX = link.source.x ?? 0
  const sourceY = link.source.y ?? 0
  const targetX = link.target.x ?? 0
  const targetY = link.target.y ?? 0
  const dx = targetX - sourceX
  const dy = targetY - sourceY
  const distance = Math.hypot(dx, dy)
  const minDistance = link.source.radius + link.target.radius + 8
  if (distance < minDistance) return null
  // Shorten the path so the arrowhead lands on the node boundary, not at
  // the node center. Each end uses its own node's radius.
  const unitX = dx / distance
  const unitY = dy / distance
  const sx = sourceX + unitX * link.source.radius
  const sy = sourceY + unitY * link.source.radius
  const tx = targetX - unitX * link.target.radius
  const ty = targetY - unitY * link.target.radius
  // Curve so reciprocal A→B and B→A links don't overlap. Bumped from 0.22
  // so the two arcs read as clearly separate.
  const curvature = 0.35 * link.curveSign
  const controlX = (sx + tx) / 2 - (ty - sy) * curvature
  const controlY = (sy + ty) / 2 + (tx - sx) * curvature
  // Midpoint of the quadratic Bézier at t=0.5 — used to anchor the value
  // label so it sits on the visible arc, not the chord.
  const midX = 0.25 * sx + 0.5 * controlX + 0.25 * tx
  const midY = 0.25 * sy + 0.5 * controlY + 0.25 * ty
  return {
    path: `M${sx},${sy} Q${controlX},${controlY} ${tx},${ty}`,
    midX,
    midY,
  }
}

function tooltipPosition(
  hovered: HoveredData,
): { x: number; y: number } | undefined {
  if (!hovered) return undefined
  if (hovered.type === 'node') {
    return { x: hovered.node.x ?? 0, y: hovered.node.y ?? 0 }
  }
  const sourceX = hovered.link.source.x ?? 0
  const sourceY = hovered.link.source.y ?? 0
  const targetX = hovered.link.target.x ?? 0
  const targetY = hovered.link.target.y ?? 0
  return { x: (sourceX + targetX) / 2, y: (sourceY + targetY) / 2 }
}

export { NetworkChart }
